import * as React from 'react'
import {
  Box,
  Form,
  Button,
  Typography,
  Loader,
  Snackbar,
} from 'cs-kevlar-react'
import { FormikSelect } from '../FormikSelect'
import { FormikTextField } from '../FormikTextField'
import { Formik } from 'formik'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Grid } from 'cs-kevlar-react'
import * as Yup from 'yup'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import {
  DocumentTypeV1,
  PermissionCheckTargetField,
  PermissionCheckResultsModel,
  DocumentType,
  ApiResult,
} from '../../api'
import { getDocumentTypeLabel } from 'utils'
import { usePermissionApi, usePageNotifications, useLogger } from 'hooks'
import {
  getAllPermissionCheckTargetFieldsForDocumentType,
  getDefaultPermissionCheckTargetFieldForDocumentType,
} from 'utils/enums/permissionCheckTargetField'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
    gridItem: {
      width: '100%',
    },
    footer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
    },
    actionButton: {
      marginLeft: 'auto',
      marginBottom: theme.spacing(4),
    },
    hasAllPermission: {
      textAlign: 'center',
      color: theme.palette.success.dark,
    },
    hasPermissions: {
      color: theme.palette.success.dark,
    },
    missingPermissions: {
      color: theme.palette.error.dark,
    },
    key: {
      color: theme.palette.neutralButton.main,
    },
    marginTop: {
      marginTop: theme.spacing(8),
    },
    error: {
      textAlign: 'center',
      color: theme.palette.error.dark,
    },
  }),
)

export const CheckPermissionsFormSchema = Yup.object().shape({
  type: Yup.string().required('The type field is required.'),
  id: Yup.string().required('The id field is required.'),
})

const documentTypeOptions: Option[] = [
  {
    label: getDocumentTypeLabel(DocumentTypeV1.Incident),
    key: getDocumentTypeLabel(DocumentTypeV1.Incident),
    value: DocumentType.Incident,
  },
  {
    label: getDocumentTypeLabel(DocumentTypeV1.EmergencyResource),
    key: getDocumentTypeLabel(DocumentTypeV1.EmergencyResource),
    value: DocumentType.EmergencyResource,
  },
  {
    label: getDocumentTypeLabel(DocumentTypeV1.Position),
    key: getDocumentTypeLabel(DocumentTypeV1.Position),
    value: DocumentType.Position,
  },
  {
    label: getDocumentTypeLabel(DocumentTypeV1.QuerySpecification),
    key: getDocumentTypeLabel(DocumentTypeV1.QuerySpecification),
    value: DocumentType.QuerySpecification,
  },
]

interface CheckPermissionsProps {
  customerAgencyId: string
  customerUserId: string
}

export const CheckPermissions: React.FunctionComponent<CheckPermissionsProps> = ({
  customerAgencyId,
  customerUserId,
}) => {
  if (customerAgencyId === null) return null

  const classes = useStyles({})
  const { logError } = useLogger()

  const { canAccessDocument } = usePermissionApi()

  const { dispatch: dispatchNotification } = usePageNotifications()

  const [
    results,
    setResults,
  ] = React.useState<PermissionCheckResultsModel | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [documentType, setDocumentType] = React.useState<DocumentType>(
    DocumentType.Incident,
  )

  const initialValues = {
    type: DocumentType.Incident,
    field: PermissionCheckTargetField.IncidentNumber,
    id: '',
  }

  const onSubmit = (values: any, actions: any) => {
    // Clear any previous result/error
    setResults(null)
    setError(null)

    canAccessDocument({
      customerAgencyId: customerAgencyId,
      customerUserId: customerUserId,
      target: values.type,
      targetField: values.field,
      targetFieldValue: values.id,
    })
      .then(response => {
        if (!response) return

        if (response.status === 200) {
          if (!response.data.isSuccess || response.data.errorMessage !== null)
            setError(response.data.errorMessage)

          setResults(response.data.data)
        } else {
          setResults(null)
        }
      })
      .catch(error => {
        logError(error)
        setResults(null)

        dispatchNotification({
          type: 'SetError',
          payload: 'There was an error performing your permission query.',
        })
      })
      .then(() => {
        actions.setSubmitting(false)
      })
  }

  const fieldTypeOptions = getAllPermissionCheckTargetFieldsForDocumentType(
    documentType,
  )

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        validationSchema={CheckPermissionsFormSchema}
        onSubmit={onSubmit}
      >
        {formikProps => (
          <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
            <Grid
              container
              direction="column"
              alignItems="flex-start"
              justifyContent="center"
            >
              <Grid item className={classes.gridItem}>
                <FormikSelect
                  id="CheckPermissions::DocumentType"
                  formikProps={formikProps}
                  label="Type"
                  fieldName="type"
                  fullWidth
                  options={documentTypeOptions}
                  onChange={(event, newValue) => {
                    setDocumentType(event.target.value as DocumentType)
                    formikProps.setFieldValue(
                      'field',
                      getDefaultPermissionCheckTargetFieldForDocumentType(
                        event.target.value as DocumentType,
                      ),
                    )
                    formikProps.handleChange(event)
                  }}
                />
              </Grid>
              <Grid item className={classes.gridItem}>
                <FormikSelect
                  id="CheckPermissions::FieldType"
                  formikProps={formikProps}
                  label="Field"
                  fieldName="field"
                  fullWidth
                  options={fieldTypeOptions}
                />
              </Grid>
              <Grid item className={classes.gridItem}>
                <FormikTextField
                  formikProps={formikProps}
                  fieldName="id"
                  label="Field Value"
                  fullWidth
                  autoComplete="off"
                />
              </Grid>
              <Grid item className={classes.footer}>
                <Button
                  color="primary"
                  type="submit"
                  className={classes.actionButton}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
            {formikProps.isSubmitting && (
              <Loader size="sm" text="Loading Results" />
            )}
            {error !== null && (
              <Typography className={classes.error}>{error}</Typography>
            )}
            {results !== null && (
              <Grid container>
                {(results.canAccess ||
                  results.missingPermissions === null ||
                  Object.keys(results.missingPermissions).length === 0) && (
                  <Grid item xs={12} className={classes.hasAllPermission}>
                    <Typography>User has all necessary permissions.</Typography>
                  </Grid>
                )}
                {results.missingPermissions !== null &&
                  Object.keys(results.missingPermissions).map(key => (
                    <React.Fragment key={key}>
                      <Grid item xs={2}>
                        <Typography
                          variant="body2"
                          display="inline"
                          className={classes.key}
                        >
                          Access Type
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          variant="body2"
                          display="inline"
                          className={classes.key}
                        >
                          Requires All Of
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography
                          variant="body2"
                          display="inline"
                          className={classes.key}
                        >
                          Requires One Of
                        </Typography>
                      </Grid>

                      <Grid item xs={2}>
                        <Typography variant="body2" display="inline">
                          {key}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        {(results.missingPermissions[key].all === null ||
                          results.missingPermissions[key].all.length === 0) && (
                          <Typography
                            variant="body2"
                            display="inline"
                            className={classes.hasPermissions}
                          >
                            Nothing Required
                          </Typography>
                        )}
                        {results.missingPermissions[key].all !== null &&
                          results.missingPermissions[key].all.length !== 0 && (
                            <Typography
                              variant="body2"
                              display="inline"
                              className={classes.missingPermissions}
                            >
                              {results.missingPermissions[key].all.join(', ')}
                            </Typography>
                          )}
                      </Grid>
                      <Grid item xs={5}>
                        {(results.missingPermissions[key].or === null ||
                          results.missingPermissions[key].or.length === 0) && (
                          <Typography
                            variant="body2"
                            display="inline"
                            className={classes.hasPermissions}
                          >
                            Nothing Required
                          </Typography>
                        )}
                        {results.missingPermissions[key].or !== null &&
                          results.missingPermissions[key].or.length !== 0 && (
                            <Typography
                              variant="body2"
                              display="inline"
                              className={classes.missingPermissions}
                            >
                              {results.missingPermissions[key].or.join(', ')}
                            </Typography>
                          )}
                      </Grid>
                    </React.Fragment>
                  ))}
              </Grid>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
}
